import isTruthy from '@proton/utils/isTruthy';

import type { SimpleMap } from '../interfaces';
import type {
    AttendeeComment,
    CalendarNotificationSettings,
    CreateOrUpdateCalendarEventData,
    VcalVeventComponent,
} from '../interfaces/calendar';
import type { AttendeeClearPartResult } from '../interfaces/calendar/Attendee';
import type { EncryptPartResult, SignPartResult } from '../interfaces/calendar/PartResult';
import { CALENDAR_CARD_TYPE } from './constants';

const { ENCRYPTED_AND_SIGNED, SIGNED } = CALENDAR_CARD_TYPE;

/**
 * Format the data into what the API expects.
 */
interface FormatDataArguments {
    sharedSignedPart?: SignPartResult;
    sharedEncryptedPart?: EncryptPartResult;
    sharedSessionKey?: Uint8Array<ArrayBuffer>;
    cancelledOccurrenceSignedPart?: SignPartResult;
    calendarSignedPart?: SignPartResult;
    calendarEncryptedPart?: EncryptPartResult;
    calendarSessionKey?: Uint8Array<ArrayBuffer>;
    attendeesEncryptedPart?: EncryptPartResult;
    attendeesClearPart?: AttendeeClearPartResult[];
    removedAttendeesEmails?: string[];
    attendeesEncryptedSessionKeysMap?: SimpleMap<Uint8Array<ArrayBuffer>>;
    notificationsPart?: CalendarNotificationSettings[];
    colorPart?: string;
    eventCommentsMap?: { [token: string]: AttendeeComment };
    videoConferencingDataPart?: { encryptedTitle: VcalVeventComponent['encryptedTitle'] };
}
export const formatData = ({
    sharedSignedPart,
    sharedEncryptedPart,
    sharedSessionKey,
    cancelledOccurrenceSignedPart,
    calendarSignedPart,
    calendarEncryptedPart,
    calendarSessionKey,
    notificationsPart,
    colorPart,
    videoConferencingDataPart,
    attendeesEncryptedPart,
    attendeesClearPart,
    removedAttendeesEmails,
    attendeesEncryptedSessionKeysMap,
    eventCommentsMap,
}: FormatDataArguments) => {
    const VideoConferencingData = videoConferencingDataPart?.encryptedTitle?.value
        ? { MeetEncryptedTitle: videoConferencingDataPart?.encryptedTitle?.value }
        : null;
    const result: Omit<CreateOrUpdateCalendarEventData, 'Permissions'> = {
        Notifications: notificationsPart || null,
        Color: colorPart || null,
        VideoConferencingData,
    };

    if (sharedSessionKey) {
        result.SharedKeyPacket = sharedSessionKey.toBase64();
    }

    if (sharedSignedPart && sharedEncryptedPart) {
        result.SharedEventContent = [
            {
                Type: SIGNED,
                Data: sharedSignedPart.data,
                Signature: sharedSignedPart.signature,
            },
            {
                Type: ENCRYPTED_AND_SIGNED,
                Data: sharedEncryptedPart.dataPacket.toBase64(),
                Signature: sharedEncryptedPart.signature,
            },
        ];
    }

    if (cancelledOccurrenceSignedPart) {
        result.CancelledOccurrenceContent = [
            {
                Type: SIGNED,
                Data: cancelledOccurrenceSignedPart.data,
                Signature: cancelledOccurrenceSignedPart.signature,
            },
        ];
    }

    if (calendarEncryptedPart && calendarSessionKey) {
        result.CalendarKeyPacket = calendarSessionKey.toBase64();
    }

    if (calendarSignedPart || calendarEncryptedPart) {
        result.CalendarEventContent = [
            calendarSignedPart && {
                Type: SIGNED,
                Data: calendarSignedPart.data,
                Signature: calendarSignedPart.signature,
            },
            calendarEncryptedPart && {
                Type: ENCRYPTED_AND_SIGNED,
                Data: calendarEncryptedPart.dataPacket.toBase64(),
                Signature: calendarEncryptedPart.signature,
            },
        ].filter(isTruthy);
    }

    if (attendeesEncryptedPart) {
        result.AttendeesEventContent = [
            {
                Type: ENCRYPTED_AND_SIGNED,
                Data: attendeesEncryptedPart.dataPacket.toBase64(),
                Signature: attendeesEncryptedPart.signature,
            },
        ];
    }

    if (attendeesClearPart) {
        result.Attendees = attendeesClearPart.map(({ token, status }) => ({
            Token: token,
            Status: status,
            Comment: eventCommentsMap?.[token] || null,
        }));
    }

    if (removedAttendeesEmails?.length) {
        result.RemovedAttendeeAddresses = removedAttendeesEmails;
    }

    if (attendeesEncryptedSessionKeysMap) {
        result.AddedProtonAttendees = Object.keys(attendeesEncryptedSessionKeysMap)
            .map((email) => {
                const sharedEncryptedSessionKey = attendeesEncryptedSessionKeysMap[email];
                if (!sharedEncryptedSessionKey) {
                    return;
                }
                return { Email: email, AddressKeyPacket: sharedEncryptedSessionKey.toBase64() };
            })
            .filter(isTruthy);
    }

    return result;
};
